import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { createProgressReporter, formatClock, watchedPercent, mediaUrl } from '../../../../shared/courses'
import { RichTextView } from '../../../../UI/RichText'
import useVideoSource, { isHlsType } from '../../../../shared/useVideoSource'

import PreviewBanner from './PreviewBanner'
import { flatLessons, useCourse } from './data'

import styles from './style.module.scss'

const COUNTDOWN = 5

/**
 * Eine Lektion abspielen.
 *
 * Schwarz und groß das Video, darunter Titel, Beschreibung und das Transkript,
 * daneben alle Folgen des Kurses. Am Ende zählt ein Fenster fünf Sekunden bis
 * zur nächsten Folge herunter — oder bietet das Quiz an, wenn das Modul fertig
 * ist.
 *
 * **Gemeldet wird höchstens alle zehn Sekunden**, bei Pause und am Ende sofort
 * (`createProgressReporter`). Ob eine Lektion als gesehen gilt, rechnet der
 * Server: Vorspulen zählt dort nicht. Die Oberfläche übernimmt nur, was er
 * zurückschickt.
 *
 * Die Adresse des Videos ist signiert und gilt einige Stunden; sie steht
 * deshalb direkt im `<video src>`. Ein angemeldeter Abruf als Blob ginge auch,
 * hieße aber, die ganze Datei zu laden, bevor das erste Bild kommt.
 */
/*
 * Textlektionen sind dreierlei, und so sollen sie auch aussehen: ein kurzer
 * Lesetext, eine Übung für die Klasse (Karteikarten, Gruppenarbeit — aus dem
 * Kursmaterial von JA Worldwide) oder Fragen zum Nachdenken. Die Art steht im
 * Titel, ein eigenes Feld wäre für drei Fälle zu viel.
 */
export const textArt = (titel = '') => {
	if (/^Übung\b/i.test(titel)) return { key: 'EXERCISE', label: 'Class exercise' }
	if (/^Zum Nachdenken/i.test(titel)) return { key: 'REFLECTION', label: 'To reflect on' }
	return { key: 'READING', label: 'Reading' }
}

/*
 * Wie lange es dauert: Bei einer Übung steht die Zeit im Text („15-20 Minuten“),
 * sonst rechnen wir mit 180 Wörtern je Minute.
 */
export const lesedauer = (html = '', t = text => text) => {
	const text = String(html || '').replace(/<[^>]+>/g, ' ')
	const spanne = text.match(/(\d+)\s*[-–]\s*(\d+)\s*Minuten/)
	if (spanne) return t('{{from}}–{{to}} min in class', { from: spanne[1], to: spanne[2] })
	const woerter = text.split(/\s+/).filter(Boolean).length
	if (!woerter) return null
	return t('{{n}} min read', { n: Math.max(1, Math.round(woerter / 180)) })
}

/*
 * Das Kursmaterial (JA Worldwide, aus EdApp übernommen) kennt keine
 * Zwischenüberschriften, nur fette Absätze („Zeit:“, „Schritt 1 …“), und
 * Hinweise als fett eingeleiteten Absatz. Für die Darstellung werden daraus
 * echte Überschriften (h4) und Hinweiskästen (kursiver Absatz) — beides
 * Auszeichnungen, die die Textanzeige zulässt. Gespeichert bleibt das Original.
 */
export const aufbereiten = (html = '') => {
	if (!html || typeof DOMParser === 'undefined') return html || ''
	const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
	const wurzel = doc.body.firstElementChild
	wurzel.querySelectorAll('p').forEach(absatz => {
		const text = absatz.textContent.trim()
		if (!text) return absatz.remove()
		const fett = absatz.querySelector(':scope > strong, :scope > b')
		if (!fett) return undefined
		const fettText = fett.textContent.trim()
		if (fettText === text && text.length <= 80) {
			const h = doc.createElement('h4')
			h.textContent = text.replace(/:$/, '')
			absatz.replaceWith(h)
		} else if (/^Hinweis\b/i.test(fettText) && absatz.firstElementChild === fett) {
			const em = doc.createElement('em')
			while (absatz.firstChild) em.appendChild(absatz.firstChild)
			absatz.appendChild(em)
		}
		return undefined
	})
	return wurzel.innerHTML
}

export default function LessonPlayer() {
	const { t } = useTranslation()
	const history = useHistory()
	const { id: courseId, lessonId } = useParams()

	const { course, reload } = useCourse(courseId)
	const [data, setData] = useState(null)
	const [fehler, setFehler] = useState(false)
	// Zählt „Erneut versuchen" hoch und löst damit das Laden noch einmal aus.
	const [versuch, setVersuch] = useState(0)
	const [progress, setProgress] = useState(null)
	const [ende, setEnde] = useState(false)
	const [rest, setRest] = useState(COUNTDOWN)

	const videoRef = useRef(null)
	const vorschau = course?.mode === 'PREVIEW' || data?.mode === 'PREVIEW'
	const vorschauRef = useRef(vorschau)
	vorschauRef.current = vorschau

	useEffect(() => {
		setData(null)
		setFehler(false)
		setEnde(false)
		setRest(COUNTDOWN)
		http.get(TApi.COURSE_LESSON(lessonId), { disableToast: true })
			.then(({ data: antwort }) => {
				setData(antwort)
				setProgress(antwort.lesson?.progress || null)
			})
			.catch(() => setFehler(true))
	}, [lessonId, versuch])

	// Der Melder hängt an der Lektion; ein Wechsel beginnt neu.
	const reporter = useMemo(
		() =>
			createProgressReporter({
				send: payload => {
					if (vorschauRef.current) return null
					return http
						.post(TApi.COURSE_LESSON_PROGRESS(lessonId), payload, { disableToast: true })
						.then(({ data: antwort }) => {
							if (antwort?.progress) setProgress(antwort.progress)
							return antwort
						})
				}
			}),
		[lessonId]
	)

	// Beim Verlassen der Seite noch einmal melden, was gesehen wurde.
	useEffect(() => {
		const video = videoRef.current
		return () => {
			if (video && video.duration) reporter.flush(video.currentTime, video.duration)
		}
	}, [reporter, data])

	const lesson = data?.lesson
	useVideoSource(videoRef, lesson?.playback?.url, lesson?.playback?.type)
	const naechste = data?.next
	const quizModul = data?.nextQuizModuleId

	// Herunterzählen bis zur nächsten Folge.
	useEffect(() => {
		if (!ende || quizModul || !naechste) return undefined
		if (rest <= 0) {
			history.push(TRoutes.COURSE_LESSON(courseId, naechste.id))
			return undefined
		}
		const timer = setTimeout(() => setRest(wert => wert - 1), 1000)
		return () => clearTimeout(timer)
	}, [ende, rest, naechste, quizModul, courseId, history])

	const wennGeladen = () => {
		const video = videoRef.current
		// Fortgesetzt wird an der zuletzt gemeldeten Stelle, nicht an der weitesten:
		// Wer zurückgespult hat, um etwas noch einmal zu sehen, will dort weiter.
		const start = Number(progress?.lastPosition ?? progress?.maxPosition) || 0
		// Fortsetzen, wo man war — außer man war schon am Ende, dann von vorn.
		if (video && start > 5 && video.duration && start < video.duration - 5) video.currentTime = start
	}

	const wennEnde = () => {
		const video = videoRef.current
		if (video?.duration) {
			const meldung = reporter.ended(video.duration)
			Promise.resolve(meldung).then(() => reload())
		}
		setEnde(true)
		setRest(COUNTDOWN)
	}

	const gelesen = () => {
		if (vorschau) return
		http.post(TApi.COURSE_LESSON_PROGRESS(lessonId), { read: true })
			.then(({ data: antwort }) => {
				if (antwort?.progress) setProgress(antwort.progress)
				return reload()
			})
			.catch(() => {})
	}

	const zurKursseite = () => history.push(TRoutes.COURSE(courseId))
	const lessons = flatLessons(course)

	return (
		<div className={styles.root}>
			{vorschau && <PreviewBanner />}

			<div className={styles.player}>
				<div className={styles.stage}>
					<div className={styles.topBar}>
						<button type="button" className={styles.backLink} onClick={zurKursseite}>
							‹ {course?.title || t('Back to the course')}
						</button>
					</div>

					{/*
					 * Meist nur kurz: Beim Ausliefern startet der Server neu und antwortet
					 * eine Minute lang nicht. Ein Knopf statt „Seite neu laden".
					 */}
					{fehler && (
						<div className={styles.error}>
							<p>{t('This lesson could not be loaded.')}</p>
							<button type="button" className={styles.retry} onClick={() => setVersuch(n => n + 1)}>
								{t('Try again')}
							</button>
						</div>
					)}
					{!fehler && !lesson && <p className={styles.loading}>{t('Loading…')}</p>}

					{lesson && lesson.kind === 'VIDEO' && (
						<div className={styles.videoBox}>
							{lesson.playback?.url ? (
								// eslint-disable-next-line jsx-a11y/media-has-caption
								<video
									key={lesson.id}
									ref={videoRef}
									// HLS (Bunny) hängt useVideoSource an, MP4 steht direkt im src.
									src={isHlsType(lesson.playback.type) ? undefined : mediaUrl(lesson.playback.url)}
									poster={mediaUrl(lesson.thumbUrl) || undefined}
									controls
									autoPlay
									playsInline
									preload="metadata"
									controlsList="nodownload"
									onContextMenu={event => event.preventDefault()}
									onLoadedMetadata={wennGeladen}
									onTimeUpdate={event =>
										reporter.tick(event.currentTarget.currentTime, event.currentTarget.duration)
									}
									onPause={event =>
										!event.currentTarget.ended &&
										reporter.flush(event.currentTarget.currentTime, event.currentTarget.duration)
									}
									onPlay={() => setEnde(false)}
									onEnded={wennEnde}
								/>
							) : (
								<div className={styles.noVideo}>{t('No video has been uploaded for this lesson yet.')}</div>
							)}

							{ende && (quizModul || naechste) && (
								<div className={styles.nextOverlay} role="dialog" aria-live="polite">
									{quizModul ? (
										<>
											<span className={styles.nextLabel}>{t('Module finished')}</span>
											<p className={styles.nextTitle}>{t('Now show what you have learned.')}</p>
											<div className={styles.nextActions}>
												<button
													type="button"
													className={styles.play}
													onClick={() => history.push(TRoutes.COURSE_QUIZ(courseId, quizModul))}
												>
													{t('To the quiz')}
												</button>
												<button type="button" className={styles.ghost} onClick={() => setEnde(false)}>
													{t('Close')}
												</button>
											</div>
										</>
									) : (
										<>
											<span className={styles.nextLabel}>
												{t('Next episode in {{n}}', { n: rest })}
											</span>
											<p className={styles.nextTitle}>{naechste.title}</p>
											<div className={styles.countdown} aria-hidden="true">
												<span style={{ width: `${((COUNTDOWN - rest) / COUNTDOWN) * 100}%` }} />
											</div>
											<div className={styles.nextActions}>
												<button
													type="button"
													className={styles.play}
													onClick={() => history.push(TRoutes.COURSE_LESSON(courseId, naechste.id))}
												>
													{t('Play now')}
												</button>
												<button type="button" className={styles.ghost} onClick={() => setEnde(false)}>
													{t('Cancel')}
												</button>
											</div>
										</>
									)}
								</div>
							)}
						</div>
					)}

					{lesson && lesson.kind === 'TEXT' && (
						<div className={styles.textHero}>
							{lesson.thumbUrl ? (
								<img className={styles.textHeroImage} src={mediaUrl(lesson.thumbUrl)} alt="" />
							) : (
								<div className={styles.textHeroImage} aria-hidden="true" />
							)}
							<div className={styles.textHeroMeta}>
								<span className={styles.textKind}>{t(textArt(lesson.title).label)}</span>
								{lesedauer(lesson.body) && <span className={styles.textDuration}>{lesedauer(lesson.body, t)}</span>}
							</div>
						</div>
					)}

					{lesson && (
						<div className={styles.lessonInfo}>
							<h1 className={styles.lessonTitle}>{lesson.title}</h1>
							{lesson.description && <p className={styles.lessonText}>{lesson.description}</p>}

							{progress?.completed && (
								<span className={styles.doneHint}>✓ {t('Watched')}</span>
							)}
							{!progress?.completed && lesson.kind === 'VIDEO' && course?.minWatchPercent && !vorschau && (
								<p className={styles.rowMeta}>
									{t('Counts as watched from {{n}} % — skipping ahead does not count.', {
										n: course.minWatchPercent
									})}
								</p>
							)}

							{lesson.transcript && (
								<details className={styles.transcript}>
									<summary>{t('Transcript')}</summary>
									<p>{lesson.transcript}</p>
								</details>
							)}
						</div>
					)}

					{lesson && lesson.kind === 'TEXT' && (
						<>
							<article className={styles.textLesson}>
								<RichTextView className={styles.textBody} value={aufbereiten(lesson.body)} />
							</article>
							<div className={styles.lessonInfo}>
								<div className={styles.actions}>
									{!progress?.completed && !vorschau && (
										<button type="button" className={styles.play} onClick={gelesen}>
											{t('Mark as read')}
										</button>
									)}
									{quizModul && (
										<button
											type="button"
											className={styles.ghost}
											onClick={() => history.push(TRoutes.COURSE_QUIZ(courseId, quizModul))}
										>
											{t('To the quiz')}
										</button>
									)}
									{!quizModul && naechste && (
										<button
											type="button"
											className={styles.ghost}
											onClick={() => history.push(TRoutes.COURSE_LESSON(courseId, naechste.id))}
										>
											{t('Next lesson')} ›
										</button>
									)}
								</div>
							</div>
						</>
					)}
				</div>

				<aside className={styles.episodes} aria-label={t('Episodes')}>
					<div className={styles.episodesHead}>{t('Episodes')}</div>
					{(course?.modules || []).map((modul, index) => (
						<div key={modul.id}>
							<div className={styles.episodeModule}>
								{t('Module {{n}}', { n: index + 1 })} · {modul.title}
							</div>
							{(modul.lessons || []).map(eintrag => {
								const aktiv = String(eintrag.id) === String(lessonId)
								const prozent = eintrag.progress?.completed
									? 100
									: watchedPercent(eintrag.progress?.maxPosition, eintrag.durationSec)
								return (
									<button
										key={eintrag.id}
										type="button"
										className={clsx(styles.episode, aktiv && styles.episodeActive)}
										aria-current={aktiv ? 'true' : undefined}
										onClick={() => history.push(TRoutes.COURSE_LESSON(courseId, eintrag.id))}
									>
										<span
											className={styles.episodeThumb}
											style={eintrag.thumbUrl ? { backgroundImage: `url("${mediaUrl(eintrag.thumbUrl)}")` } : undefined}
										>
											<span className={styles.cardBar}>
												<span style={{ width: `${prozent}%` }} />
											</span>
										</span>
										<span>
											<span className={styles.episodeTitle}>
												{eintrag.progress?.completed ? '✓ ' : ''}
												{eintrag.title}
											</span>
											<span className={styles.episodeMeta}>
												{eintrag.kind === 'TEXT'
													? t('Reading')
													: eintrag.durationSec
													? formatClock(eintrag.durationSec)
													: t('Video')}
											</span>
										</span>
									</button>
								)
							})}
							{modul.quiz?.available && (
								<button
									type="button"
									className={styles.episode}
									disabled={!modul.quiz.unlocked}
									onClick={() => history.push(TRoutes.COURSE_QUIZ(courseId, modul.id))}
								>
									<span className={clsx(styles.episodeThumb, styles.quizThumb)}>
										{modul.quiz.passed ? '★' : '?'}
									</span>
									<span>
										<span className={styles.episodeTitle}>{t('Quiz')}</span>
										<span className={styles.episodeMeta}>
											{modul.quiz.passed
												? t('Passed')
												: modul.quiz.unlocked
												? t('Ready to start')
												: t('Unlocks after all lessons')}
										</span>
									</span>
								</button>
							)}
						</div>
					))}
					{lessons.length === 0 && course && <p className={styles.loading}>{t('No lessons yet.')}</p>}
				</aside>
			</div>
		</div>
	)
}
